/**
 * @module components/common/SortByPanel/DatasetFieldSort
 * @description X/Y 轴「字段排序」子面板 — 字段 / 聚合方式 / 升降序
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { Radio, Select } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_EN } from '@home/_config/config';
import { MetricTypes } from '@dbl/core/dataSource';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';
import type { IMetricSortValueItem } from '../../../../types/settings/sortFields';
import {
  buildDatasetFieldSelectOptions,
  DATASET_FIELD_AGGREGATED_SELECT_VALUE,
  fromAggregateSelectValue,
  getDatasetFieldAggregateOptions,
  matchDatasetFieldSearchKeyword,
  normalizeDatasetFieldSortValue,
  pickDefaultAggregate,
  resolveSelectValueFromSortItem,
  resolveSortItemAggregate,
  toAggregateSelectValue,
  toDatasetFieldSortItem,
  withDatasetFieldSortAggregate,
  withDatasetFieldSortOrder,
} from './utils';
import DatasetFieldOptionText from './DatasetFieldOptionText';
import './index.scss';

export interface IDatasetFieldSortProps {
  value: IMetricSortValueItem[];
  onChange: (value: IMetricSortValueItem[]) => void;
  dataTables: IGetTableInfoDto[];
  customColumns: CustomColumn[];
}

const DatasetFieldSort: React.FC<IDatasetFieldSortProps> = ({
  value,
  onChange,
  dataTables,
  customColumns,
}) => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const current = value?.[0];

  const { options: fieldOptionData, groups, metaByValue } = useMemo(
    () =>
      buildDatasetFieldSelectOptions({
        dataTables: dataTables || [],
        customColumns: customColumns || [],
        isEn,
        customGroupLabel: formatMessage({
          id: 'dbl_chart_dataset_field_sort_custom_group',
        }),
      }),
    [dataTables, customColumns, isEn, formatMessage],
  );

  const fieldSelectOptions = useMemo(
    () =>
      fieldOptionData.map(item => ({
        value: item.value,
        ...(item.groupKey ? { groupKey: item.groupKey } : {}),
        text: (
          <DatasetFieldOptionText
            displayName={item.meta.displayName}
            columnType={String(item.meta.columnType || '')}
          />
        ),
      })),
    [fieldOptionData],
  );

  const fieldSelectValue = useMemo(
    () => resolveSelectValueFromSortItem(current, metaByValue),
    [current, metaByValue],
  );

  const selectedMeta = metaByValue[fieldSelectValue];

  const aggregateOptions = useMemo(
    () =>
      getDatasetFieldAggregateOptions(
        selectedMeta?.columnType,
        isEn,
        selectedMeta?.isAggregate,
      ).map(item => ({
        // tea Select 对 value='' 不稳定：已聚合用哨兵值，回写仍上传 ''
        value: item.value === MetricTypes.aggregated.value
          ? DATASET_FIELD_AGGREGATED_SELECT_VALUE
          : item.value,
        text: (
          <span
            title={item.text}
            className="dbl-chart-dataset-field-sort__option-text"
          >
            {item.text}
          </span>
        ),
      })),
    [selectedMeta?.columnType, selectedMeta?.isAggregate, isEn],
  );

  /** 无有效字段默认首项；非法聚合回落 —— 单次 normalize，仅在有变更时 onChange */
  useEffect(() => {
    const normalized = normalizeDatasetFieldSortValue({
      current,
      fieldOptionData,
      metaByValue,
    });
    if (!normalized) {
      return;
    }
    onChange(normalized);
  }, [current, fieldOptionData, metaByValue, onChange]);

  const handleFieldChange = useCallback(
    (nextValue: string) => {
      const meta = metaByValue[nextValue];
      if (!meta) {
        return;
      }
      onChange([
        toDatasetFieldSortItem(
          meta,
          pickDefaultAggregate(meta.columnType, meta.isAggregate),
          'asc',
        ),
      ]);
    },
    [metaByValue, onChange],
  );

  /** 与 TableFieldList.Search 一致：toLowerCase + indexOf，匹配展示名 / 字段名 */
  const handleFieldFilter = useCallback(
    (inputValue: string, option: { value?: string }) => {
      const meta = option?.value ? metaByValue[option.value] : undefined;
      return matchDatasetFieldSearchKeyword(meta?.searchTexts, inputValue);
    },
    [metaByValue],
  );

  const handleAggregateChange = useCallback(
    (nextAggregateSelect: string) => {
      const nextAggregate = fromAggregateSelectValue(nextAggregateSelect);
      if (selectedMeta) {
        onChange([
          toDatasetFieldSortItem(
            selectedMeta,
            nextAggregate,
            current?.value === 'desc' ? 'desc' : 'asc',
          ),
        ]);
        return;
      }
      if (!current?.key) {
        return;
      }
      onChange([withDatasetFieldSortAggregate(current, nextAggregate)]);
    },
    [selectedMeta, current, onChange],
  );

  const handleOrderChange = useCallback(
    (nextOrder: string) => {
      const order: 'asc' | 'desc' = nextOrder === 'desc' ? 'desc' : 'asc';
      if (selectedMeta) {
        onChange([
          toDatasetFieldSortItem(
            selectedMeta,
            resolveSortItemAggregate(current, selectedMeta),
            order,
          ),
        ]);
        return;
      }
      if (!current?.key) {
        return;
      }
      onChange([withDatasetFieldSortOrder(current, order)]);
    },
    [selectedMeta, current, onChange],
  );

  return (
    <div className="dbl-chart-dataset-field-sort">
      <div className="dbl-chart-dataset-field-sort__fields">
        <div className="dbl-chart-dataset-field-sort__row">
          <span className="dbl-chart-dataset-field-sort__label">
            {formatMessage({ id: 'dbl_chart_dataset_field_sort_field' })}
          </span>
          <Select
            appearance="button"
            matchButtonWidth
            className="dbl-chart-dataset-field-sort__select"
            overlayClassName="dbl-chart-dataset-field-sort__overlay"
            listWidth={280}
            value={fieldSelectValue || undefined}
            options={fieldSelectOptions}
            groups={Object.keys(groups).length > 0 ? groups : undefined}
            searchable
            filter={handleFieldFilter}
            placeholder={formatMessage({
              id: 'dbl_chart_dataset_field_sort_placeholder',
            })}
            onChange={handleFieldChange}
          />
        </div>
        <div className="dbl-chart-dataset-field-sort__row">
          <span className="dbl-chart-dataset-field-sort__label">
            {formatMessage({ id: 'dbl_chart_dataset_field_sort_agg' })}
          </span>
          <Select
            appearance="button"
            matchButtonWidth
            className="dbl-chart-dataset-field-sort__select"
            overlayClassName="dbl-chart-dataset-field-sort__overlay"
            value={toAggregateSelectValue(resolveSortItemAggregate(current, selectedMeta))}
            options={aggregateOptions}
            placeholder={formatMessage({
              id: 'dbl_chart_dataset_field_sort_placeholder',
            })}
            onChange={handleAggregateChange}
          />
        </div>
      </div>
      <div
        className={classNames(
          'dbl-chart-dataset-field-sort__order',
          {
            'dbl-chart-dataset-field-sort__order--en': isEn,
            'dbl-chart-dataset-field-sort__order--zh': !isEn,
          },
        )}
      >
        <Radio.Group
          value={current?.value || 'asc'}
          onChange={handleOrderChange}
        >
          <Radio name="asc">
            {formatMessage({ id: 'dbl_chart_low' })}
            <SvgIcon type="arrow-big-up" className="dbl-sort-rate-icon-arrow" />
          </Radio>
          <Radio name="desc">
            {formatMessage({ id: 'dbl_chart_high' })}
            <SvgIcon
              type="arrow-big-down"
              className="dbl-sort-rate-icon-arrow"
            />
          </Radio>
        </Radio.Group>
      </div>
    </div>
  );
};

export default React.memo(DatasetFieldSort);
